/**
 * Proactive session refresh for web.
 *
 * `SdkAuthProvider` reaches `POST /v1/auth/refresh` only after `GET /v1/me`
 * answers 401, and a cookie the server has already rejected cannot authorise
 * its own renewal — that path recovers a session, it cannot preserve one.
 * Keeping a session alive takes a refresh that fires while the cookie is still
 * good, which is this scheduler. React Native runs its own at
 * `platform/react-native/auth/session/refresh-scheduler.ts`.
 *
 * **This scheduler must never send `mode: 'rotate'`.** Rotation revokes the
 * current session and returns its successor only as `Set-Cookie`; a navigation
 * aborted mid-round-trip discards that response, leaving the browser holding a
 * revoked token and the user permanently signed out. `mode: 'extend'` keeps the
 * jti and revokes nothing, so a dropped response costs one wasted round-trip.
 * Refreshing ahead of need is safe only under those semantics.
 *
 * Multi-tab needs no coordination for the same reason: concurrent extends are
 * idempotent, so every tab may run its own timer.
 *
 * The deadline is learned rather than assumed. `morfi_session` is httpOnly and
 * the login response carries no expiry, so the first refresh is what reveals
 * it; it is then persisted and every later refresh updates it.
 */

import { SESSION_REFRESH_REMAINING_MS } from '../../../../auth/session-policy.js';

// ── Tunables ─────────────────────────────────────────────────────────────────

/** Refresh once less than this remains — the remaining-life form of the
 *  shared rolling rule in `session-policy.ts`, since the httpOnly cookie
 *  reveals its expiry but not its issue time. */
const REFRESH_LEAD_MS = SESSION_REFRESH_REMAINING_MS;

/** Never arm a timer shorter than this — guards against a busy-loop if the
 *  server hands back an expiry that is already inside the lead window. */
const MIN_TIMER_MS = 60 * 1000;

const EXPIRY_STORAGE_KEY = 'morfi_session_expires_at';

// ── Module state ─────────────────────────────────────────────────────────────

interface SchedulerState {
  timer: ReturnType<typeof setTimeout> | null;
  onVisibility: (() => void) | null;
  inFlight: Promise<void> | null;
}

const state: SchedulerState = { timer: null, onVisibility: null, inFlight: null };

// ── Persisted expiry ─────────────────────────────────────────────────────────

/** Stored deadline in epoch ms, or `null` when unknown or unreadable. */
function readStoredExpiry(): number | null {
  try {
    const raw = localStorage.getItem(EXPIRY_STORAGE_KEY);
    if (raw === null) return null;
    const parsed = Date.parse(raw);
    return Number.isNaN(parsed) ? null : parsed;
  } catch {
    // SILENT-CATCH: localStorage may be blocked (private mode, strict
    // settings). An unknown expiry degrades to "refresh once to learn it".
    return null;
  }
}

/**
 * Record a deadline learned outside the scheduler — the restore path already
 * refreshes when `/v1/me` rejects the cookie, and that response carries the
 * new `expiresAt`. Handing it over here stops the scheduler asking the server
 * a question it has just been told the answer to.
 */
export function recordSessionExpiry(expiresAt: string): void {
  // Ignore anything unparseable rather than persisting it: a stored value that
  // reads back as NaN is indistinguishable from "unknown", which would send
  // the scheduler into a refresh on every single start.
  if (Number.isNaN(Date.parse(expiresAt))) return;
  writeStoredExpiry(expiresAt);
}

function writeStoredExpiry(expiresAt: string): void {
  try {
    localStorage.setItem(EXPIRY_STORAGE_KEY, expiresAt);
  } catch {
    // SILENT-CATCH: as above — the scheduler still works for this tab's
    // lifetime, it just re-learns the expiry after a reload.
  }
}

/**
 * Drop the stored deadline after a rejected refresh. Logout does not call this
 * — `clearUserStorage()` already lists the key.
 */
function clearStoredSessionExpiry(): void {
  try {
    localStorage.removeItem(EXPIRY_STORAGE_KEY);
  } catch {
    // SILENT-CATCH: nothing to clean up if storage is unavailable.
  }
}

// ── Public surface ───────────────────────────────────────────────────────────

export interface RefreshSchedulerDeps {
  /**
   * `refreshMut.mutateAsync` from `SdkAuthProvider`. Must resolve with the
   * endpoint's `expiresAt` so the next timer can be armed.
   */
  readonly refreshAsync: () => Promise<{ readonly expiresAt: string }>;
  /**
   * Called when a refresh is rejected outright — the session is gone and the
   * provider should drop to signed-out. Transport failures never reach this.
   */
  readonly onSessionRejected?: () => void;
  /** Optional structured sink; never receives tokens. */
  readonly onDiagnostic?: (event: { trigger: string; outcome: string }) => void;
}

/**
 * Start the scheduler. Idempotent — a second call replaces the first. Returns
 * the teardown to run on unmount or logout.
 */
export function startRefreshScheduler(deps: RefreshSchedulerDeps): () => void {
  stopRefreshScheduler();

  const onVisibility = (): void => {
    if (document.visibilityState !== 'visible') return;
    // A sleeping laptop stalls `setTimeout`, so the timer alone cannot be
    // trusted to have fired. Re-evaluate against the wall clock on every
    // return to the tab.
    evaluate(deps, 'foreground');
  };
  document.addEventListener('visibilitychange', onVisibility);
  state.onVisibility = onVisibility;

  evaluate(deps, 'start');

  return stopRefreshScheduler;
}

/** Stop the scheduler. Safe to call when it was never started. */
export function stopRefreshScheduler(): void {
  if (state.timer !== null) {
    clearTimeout(state.timer);
    state.timer = null;
  }
  if (state.onVisibility !== null) {
    document.removeEventListener('visibilitychange', state.onVisibility);
    state.onVisibility = null;
  }
  state.inFlight = null;
}

// ── Internals ────────────────────────────────────────────────────────────────

/** Refresh now, or arm a timer for when the lead window opens. */
function evaluate(deps: RefreshSchedulerDeps, trigger: string): void {
  const expiry = readStoredExpiry();

  // Unknown deadline — one refresh both extends the session and teaches us
  // when it now ends. Cheap and idempotent, so the bootstrap costs nothing.
  if (expiry === null) {
    void runRefresh(deps, trigger);
    return;
  }

  const remaining = expiry - Date.now();
  if (remaining <= REFRESH_LEAD_MS) {
    void runRefresh(deps, trigger);
    return;
  }

  armTimer(deps, remaining - REFRESH_LEAD_MS);
}

function armTimer(deps: RefreshSchedulerDeps, delayMs: number): void {
  if (state.timer !== null) clearTimeout(state.timer);
  state.timer = setTimeout(
    () => {
      state.timer = null;
      void runRefresh(deps, 'scheduled');
    },
    Math.max(delayMs, MIN_TIMER_MS),
  );
}

/** Single-flight refresh — concurrent triggers share one round-trip. */
async function runRefresh(deps: RefreshSchedulerDeps, trigger: string): Promise<void> {
  if (state.inFlight !== null) return state.inFlight;

  const pending = (async (): Promise<void> => {
    try {
      const { expiresAt } = await deps.refreshAsync();
      writeStoredExpiry(expiresAt);
      deps.onDiagnostic?.({ trigger, outcome: 'refreshed' });
      const remaining = Date.parse(expiresAt) - Date.now();
      if (!Number.isNaN(remaining)) armTimer(deps, remaining - REFRESH_LEAD_MS);
    } catch (error) {
      if (isAuthRejection(error)) {
        clearStoredSessionExpiry();
        deps.onDiagnostic?.({ trigger, outcome: 'rejected' });
        deps.onSessionRejected?.();
        return;
      }
      // Transport or server failure: the session is probably still fine, so
      // hold the credential and let the next foreground return try again.
      // No retry ladder — a web tab gets a visibility trigger for free.
      deps.onDiagnostic?.({ trigger, outcome: 'deferred' });
    }
  })();

  state.inFlight = pending;
  try {
    await pending;
  } finally {
    if (state.inFlight === pending) state.inFlight = null;
  }
}

/** A 401/403 means the session is genuinely gone; anything else is transport. */
function isAuthRejection(error: unknown): boolean {
  if (error === null || typeof error !== 'object') return false;
  const status = (error as { status?: unknown }).status;
  return status === 401 || status === 403;
}
